import { BaseService } from './BaseService';
import { apiService } from './apiService';
import { WellbeingReportsResponse, ReportsParams, FeedbacksReportsResponse, FeedbacksReportsParams, DauReportsResponse, ReportTenantsResponse } from '../types/reports';

export class ReportService extends BaseService {
  constructor() {
    super('ReportService', { debug: false });
  }

  /**
   * Get wellbeing reports data
   */
  async getWellbeingReports(params: ReportsParams = {}): Promise<WellbeingReportsResponse> {
    return this.executeOperation(async () => {
      const queryParams = new URLSearchParams();
      
      if (params.startDay) {
        queryParams.append('startDay', params.startDay);
      }
      
      if (params.endDay) {
        queryParams.append('endDay', params.endDay);
      }

      if (params.provider) {
        queryParams.append('provider', params.provider);
      }

      if (params.tenant) {
        queryParams.append('tenant', params.tenant);
      }
      
      const endpoint = `/api/reports/wellbeing${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      return await apiService.get<WellbeingReportsResponse>(endpoint);
    }, 'getWellbeingReports');
  }

  /**
   * Get daily active users (DAU) reports data
   * Note: This method does not use retry logic to prevent continuous failures
   */
  async getDauReports(params: { startDate?: string; endDate?: string; provider?: string; tenant?: string } = {}): Promise<DauReportsResponse> {
    // Directly call apiService without executeOperation wrapper to avoid any retry logic
    try {
      const queryParams = new URLSearchParams();
      
      if (params.startDate) {
        queryParams.append('startDate', params.startDate);
      }
      
      if (params.endDate) {
        queryParams.append('endDate', params.endDate);
      }

      if (params.provider) {
        queryParams.append('provider', params.provider);
      }

      if (params.tenant) {
        queryParams.append('tenant', params.tenant);
      }

      const endpoint = `/api/reports/users/dau${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const result = await apiService.get<DauReportsResponse>(endpoint);
      
      return result;
    } catch (error) {
      // Create a more user-friendly error message for 500 errors
      let friendlyMessage = 'Failed to fetch DAU reports';
      if (error instanceof Error && error.message.includes('500')) {
        friendlyMessage = 'Server error: DAU reports are temporarily unavailable';
      } else if (error instanceof Error) {
        friendlyMessage = error.message;
      }
      
      const friendlyError = new Error(friendlyMessage);
      
      throw friendlyError;
    }
  }

  /**
   * Get feedback reports data
   */
  async getFeedbackReports(params: FeedbacksReportsParams = {}): Promise<FeedbacksReportsResponse> {
    return this.executeOperation(async () => {
      const queryParams = new URLSearchParams();
      
      if (params.startDay) {
        queryParams.append('startDay', params.startDay);
      }
      
      if (params.endDay) {
        queryParams.append('endDay', params.endDay);
      }
      
      if (params.includeAllVersions !== undefined) {
        queryParams.append('includeAllVersions', params.includeAllVersions.toString());
      }

      if (params.provider) {
        queryParams.append('provider', params.provider);
      }

      if (params.tenant) {
        queryParams.append('tenant', params.tenant);
      }
      
      const endpoint = `/api/reports/feedbacks${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      return await apiService.get<FeedbacksReportsResponse>(endpoint);
    }, 'getFeedbackReports');
  }

  /**
   * Get available tenants for the current user
   */
  async getAvailableTenants(): Promise<ReportTenantsResponse> {
    return this.executeOperation(async () => {
      return await apiService.get<ReportTenantsResponse>('/api/reports/tenants');
    }, 'getAvailableTenants');
  }

  // Circuit breaker and retry methods required by BaseService
  protected onCircuitBreakerChange(eventType: string, data: any): void {
    this.logDebug(`Circuit breaker event: ${eventType}`, data);
  }

  protected onRetry(error: Error, attempt: number, delay: number, operationName: string): void {
    this.logDebug(`Retrying operation: ${operationName}`, {
      attempt,
      delay,
      error: error.message
    });
  }
}

// Export singleton instance
export const reportService = new ReportService();